/**
 * FaqPage — public FAQs from the landing-page nav, split into the two audiences (families /
 * professionals & providers). Answers must stay true to what's actually built and priced —
 * pricing mirrors the paywalls, the Xplan answer promises a roadmap (not a feature), and the
 * SOA-scope answer states the strategic-advice boundary the owner set (no portfolio/fund
 * selection; advisers can issue their own limited-scope SOA for fund adjustments).
 */
import React, { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft, HelpCircle, ChevronDown, Users, Briefcase, Sparkles, ChevronRight,
} from 'lucide-react';

interface Faq {
  q: string;
  a: string;
}

const CLIENT_FAQS: Faq[] = [
  {
    q: 'What does it cost?',
    a: '$480 including GST for 4 months of full access — one payment, no subscription, no auto-renewal, and we don’t keep your card on file. Everything is included: every living arrangement, the Suggestion Engine, your Statement of Advice, and the AWise assistant. The Cost page has the full breakdown.',
  },
  {
    q: 'What happens when my 4 months end?',
    a: 'Your account locks, but nothing is deleted — your plan and information are kept safe. If you come back next month or next year, one payment reopens everything exactly where you left it. You never start again from scratch.',
  },
  {
    q: 'Is this real financial advice, or just a calculator?',
    a: 'It’s much more than a calculator. Everything is modelled on your actual situation — your home, savings, pension and goals — and the result is a personal plan with a Statement of Advice, produced under Australian financial services licensing arrangements. Calculators give you a number; AgedCareWise gives you a strategy and shows its working.',
  },
  {
    q: 'Do I still need a financial adviser?',
    a: 'Many people complete their whole plan on their own — that’s what AgedCareWise is designed for. If you already work with an adviser or accountant, they may be an AgedCareWise partner and can work with you right inside the tool. And if part of your situation needs help beyond what guided modelling can properly cover, we’ll tell you rather than guess.',
  },
  {
    q: 'Which living situations does it cover?',
    a: 'All the big ones: staying at home with Support at Home, residential aged care, retirement villages, granny flat arrangements, and downsizing. You can model several side by side and compare what each means for your money, your pension and your estate.',
  },
  {
    q: 'We’re a couple — can it handle both of us?',
    a: 'Yes — couples are a core strength, including the hardest scenario: when one of you needs care and the other stays home. AgedCareWise applies the special couple rules (like Centrelink’s more generous treatment of couples separated by illness) automatically.',
  },
  {
    q: 'How long does it take?',
    a: 'About ten minutes to tell us your story. After that, explore as much as you like — everything saves automatically, so you can leave and pick up exactly where you stopped, as often as you want, for your full 4 months.',
  },
  {
    q: 'Is my information safe?',
    a: 'Your details are stored in Australia, protected with bank-grade encryption, and never sold or used for advertising. You can download everything we hold about you, or ask us to delete it, at any time. The Security and Privacy pages explain how.',
  },
  {
    q: 'What if the rules change while I’m planning?',
    a: 'Aged-care and pension rules are updated regularly (many rates change every March and September). We keep the modelling current with the Australian rules in force, so your plan reflects today’s law — not last year’s.',
  },
];

const PARTNER_FAQS: Faq[] = [
  {
    q: 'Who is providing the advice to the client?',
    a: 'It depends on who you are. Financial advisers advise under their own AFSL — AgedCareWise is your modelling and SOA engine, and the advice is yours. Other partners (accountants, aged-care and home-care providers, retirement-living operators) aren’t giving financial advice at all: you simply provide your clients access to the tool, and the advice is provided by AgedCareWise under its licensing arrangements.',
  },
  {
    q: 'What does it cost the firm?',
    a: 'Two options: $800 + GST per year including 20 activated clients (then $70 + GST per extra client), or pay-as-you-go at $336 + GST per client with no annual commitment. Your clients never pay AgedCareWise, and we never take a share of the fees you charge them — how you price your service is entirely yours.',
  },
  {
    q: 'Does it integrate with Xplan or other planning software?',
    a: 'AgedCareWise runs alongside your existing stack today — client files and Statements of Advice export cleanly for your records and file notes. Direct practice-software integrations, including Xplan, are on our roadmap and will be prioritised by partner demand, so tell us what your workflow needs.',
  },
  {
    q: 'Can we white-label or co-brand it?',
    a: 'Your firm is front and centre in the client experience today: your practice’s identity carries through the portal, and every Statement of Advice is attributed to the adviser and licence that produced it. Deeper co-branding and full white-label arrangements are available by agreement — talk to us.',
  },
  {
    q: 'What does the Statement of Advice cover — and what doesn’t it?',
    a: 'The SOA covers the strategic decision: the living arrangement, how to fund it, means-testing and Age Pension impacts, cashflow and estate outcomes, and the recommended strategy — including which assets to draw on or sell down, driven by income, growth and capital-gains-tax assumptions rather than specific product features. It deliberately does not provide portfolio construction advice. Advisers who need to adjust underlying investments as part of the strategy can issue their own limited-scope SOA for those changes alongside ours.',
  },
  {
    q: 'How much of the journey do we control — can clients self-serve?',
    a: 'That’s up to you — the tool is designed for different practice models and skill sets. Fully self-directed: send the invite link and your client (or their family) completes the whole journey themselves. Hybrid: have the client complete just the fact-find in their own time, then you take over the modelling together in a meeting. Full control: run everything in the room — and if you’d rather own the narrative yourself, simply close the Insight panel and the tool stays quiet while you do the explaining.',
  },
  {
    q: 'How does inviting clients work?',
    a: 'One click creates a secure, one-time invitation link. Your client signs in with their own private login and tells their story in their own time — and you track every file in your workspace: invited, in progress, plan ready. Prefer to sit together? You can also create and complete files with the client in the room.',
  },
  {
    q: 'We’re an aged-care or home-care provider, not an adviser — is this for us?',
    a: 'Very much so. Providers and retirement-living operators use AgedCareWise to give families clear, personal answers on funding a decision they’re often facing for the first time — which means faster, more confident decisions. You provide the access; the advice is provided by AgedCareWise.',
  },
  {
    q: 'How is our client data protected — including from other firms?',
    a: 'Strict tenant isolation is enforced by the server, not the screen: your firm can only ever see its own clients, and no other firm can ever see yours. Firm logins require two-factor authentication, every important action lands on a tamper-evident audit trail, and everything is stored encrypted in Australia.',
  },
  {
    q: 'Is there a free trial for firms?',
    a: 'No — and that’s deliberate: the tool delivers real, licensed advice value from the first client. The Experience page gives you a genuine tour of the full journey, and the pay-as-you-go option means you can start with a single client for $336 + GST and scale to the annual plan when it earns its keep.',
  },
];

const FaqItem = ({ q, a }: Faq) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="text-sm font-semibold text-slate-900 sm:text-base">{q}</span>
        <ChevronDown
          size={18}
          className={`shrink-0 text-[#1a6fd4] transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <div className="px-5 pb-5 text-sm leading-relaxed text-gray-600">{a}</div>}
    </div>
  );
};

interface FaqSectionProps {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  faqs: Faq[];
}

const FaqSection = ({ icon: Icon, title, subtitle, faqs }: FaqSectionProps) => (
  <section>
    <div className="mb-5 flex items-center gap-3">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#1a6fd4]">
        <Icon size={22} />
      </div>
      <div>
        <h2 className="text-xl font-bold text-slate-900">{title}</h2>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>
    </div>
    <div className="space-y-3">
      {faqs.map((f) => (
        <FaqItem key={f.q} {...f} />
      ))}
    </div>
  </section>
);

export interface FaqPageProps {
  onBack: () => void;
  onGetStarted?: () => void;
  onBecomePartner?: () => void;
}

const FaqPage = ({ onBack, onGetStarted, onBecomePartner }: FaqPageProps) => (
  <div className="min-h-screen bg-slate-50">
    {/* Top bar */}
    <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="text-lg font-bold">
          <span className="text-slate-900">AgedCare</span><span className="text-[#17b8e0]">Wise</span>
        </div>
      </div>
    </div>

    {/* Hero */}
    <div className="relative overflow-hidden bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] text-white">
      <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-10 bottom-0 h-48 w-48 rounded-full bg-white/5 blur-2xl" />
      <div className="relative mx-auto max-w-5xl px-5 py-14 text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
          <HelpCircle className="h-8 w-8" />
        </div>
        <h1 className="text-3xl font-bold sm:text-4xl">Questions, answered</h1>
        <p className="mx-auto mt-3 max-w-2xl text-base text-white/90 sm:text-lg">
          The things families and professionals ask us most — answered straight, in plain English.
        </p>
      </div>
    </div>

    <div className="mx-auto max-w-3xl space-y-14 px-5 py-12">
      <FaqSection
        icon={Users}
        title="For you and your family"
        subtitle="Planning your own, or a parent’s, next chapter"
        faqs={CLIENT_FAQS}
      />
      <FaqSection
        icon={Briefcase}
        title="For professionals & providers"
        subtitle="Advisers, accountants, care providers and retirement-living operators"
        faqs={PARTNER_FAQS}
      />

      {/* Closing CTA */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 p-7 text-center text-white sm:p-9">
        <h2 className="text-lg font-bold sm:text-xl">Didn’t find your question?</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-white/80">
          Ask AWise on the home page — or just step inside and see for yourself.
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          {onGetStarted && (
            <button
              type="button"
              onClick={onGetStarted}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] px-7 py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl"
            >
              <Sparkles size={16} /> Get Started
            </button>
          )}
          {onBecomePartner && (
            <button
              type="button"
              onClick={onBecomePartner}
              className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              For partners & providers <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default FaqPage;
